import React from "react"
import styled from "@emotion/styled"
import { Formik, Form as FormikForm } from "formik"
import colors from "@constants/colors"
import { typographyHelperCss } from "@constants/typography"
import VerticalSpace from "@components/VerticalSpace"
import {
  SectionTypes,
  FieldTypes,
  FieldTypesComponentMap,
  FieldProps,
} from "../../common"
import _ from "lodash"
import { Spinner } from "@chakra-ui/react"

const Container = styled.div`
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  display: flex;
  height: 100%;

  form {
    position: relative;
    width: 100%;

    &:active {
      outline: none;
    }

    &:focus {
      outline: none;
    }

    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    textarea:-webkit-autofill,
    textarea:-webkit-autofill:hover,
    textarea:-webkit-autofill:focus {
      box-shadow: 0 0 0 1000px ${colors.grayLight} inset !important;
      color: ${colors.dark} !important;
    }
  }
`

const Content = styled.div`
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  display: flex;
`

const Row = styled.div`
  flex-shrink: 0;
  width: 100%;
`

const Columns = styled.div`
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  display: flex;
  width: 100%;
`

const Column = styled.div`
  flex-direction: column;
  align-self: stretch;
  display: flex;
  width: 100%;

  @media only screen and (min-width: 1024px) {
    width: calc(50% - 20px);
  }
`

const Label = styled.div`
  ${typographyHelperCss("bodySmall")}
  color: ${colors.dark};
  font-weight: 800 !important;
  font-family: Avenir;
  padding-bottom: 1rem;

  @media only screen and (min-width: 1024px) {
    ${typographyHelperCss("paragraphLarge")}
  }
`

const FieldWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
`

const TextAreaFieldWrapper = styled.div`
  width: 100%;
  height: 100%;
  margin-bottom: 24px;
`

const ButtonWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
`

const Button = styled.button`
  align-items: center;
  flex-direction: column;
  display: flex;
  height: 58px;
  padding: 0 32px;
  border-radius: 6px;
  background: ${colors.dark};
  font-family: Avenir;
  font-weight: 800;
  font-size: 16px;
  line-height: 58px;
  text-align: center;
  text-transform: uppercase;
  color: ${colors.white};
  justify-content: center;
  align-items: center;
  min-width: 185px;

  &:disabled {
    background-color: ${colors.dark50};
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  ${typographyHelperCss("bodyXs")}
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  text-align: center;
  color: ${colors.error};
`

interface FormComposerProps {
  schema: any
  formId: string
  initialValues: any
  sections: Array<{
    type: SectionTypes
    label: string
    fields: Array<FieldProps>
  }>
  submitText: string
  onSubmit: any
  errorMessage?: string
  isLoading?: boolean
}

const renderRow = (key, label, fields) => {
  const chunks = _.chunk(fields, fields.length / 2)
  return (
    <Row key={key}>
      <Label>{label}</Label>
      <VerticalSpace height={32} mobileHeight={24} />
      <Columns>
        {chunks.map((chunk, idx) => (
          <Column key={idx}>{chunk.map(renderField)}</Column>
        ))}
      </Columns>
    </Row>
  )
}

const renderColumn = (key, label, fields) => (
  <Column key={key}>
    <Label>{label}</Label>
    {fields.map(renderField)}
  </Column>
)

const renderField = (field, fieldIndex) => {
  const WrapperComponent =
    field.type === FieldTypes.TextArea ? TextAreaFieldWrapper : FieldWrapper
  const FieldComponent = FieldTypesComponentMap[field.type]

  return (
    <WrapperComponent key={fieldIndex}>
      <FieldComponent {...field} />
    </WrapperComponent>
  )
}

const FormComposer: React.FC<FormComposerProps> = ({
  schema,
  formId,
  initialValues,
  sections,
  submitText,
  onSubmit,
  errorMessage,
  isLoading,
}) => {
  const handleSubmit = (values) => {
    onSubmit(values)
  }

  return (
    <Formik
      validationSchema={schema}
      isInitialValid={false}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {(formik) => {
        const { isValid, isSubmitting } = formik

        return (
          <Container>
            <FormikForm id={formId}>
              <Content>
                {sections.map((section, sectionIndex) => {
                  return section.type === SectionTypes.Column
                    ? renderColumn(sectionIndex, section.label, section.fields)
                    : renderRow(sectionIndex, section.label, section.fields)
                })}
              </Content>
              <VerticalSpace height={76} mobileHeight={40} />
              <ButtonWrapper>
                <Button type="submit" disabled={!isValid || isSubmitting}>
                  {isLoading ? <Spinner /> : submitText}
                </Button>
              </ButtonWrapper>
              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </FormikForm>
          </Container>
        )
      }}
    </Formik>
  )
}

export default FormComposer
